/* eslint-disable react/prop-types */
/**
 * Admin → Landmen — maintain the WIG land-team roster (BD.Admin).
 *
 * Landmen are the vocabulary behind the deal-level `warwick_landman_id` field
 * (the "Warwick Landman" picker on the deal form, which reads the active-only
 * `listLandmen` list). `warwick_landman` is its own FK reference table rather
 * than a `pick_lists` vocabulary, so it never appeared on Admin → Pick Lists
 * and had no maintenance screen at all — adding or retiring a landman meant
 * writing SQL. This screen is that missing path: add a landman, rename one,
 * set/clear their email, and activate / deactivate (soft delete). There is NO
 * hard delete, because deals reference landmen by id and history must stay
 * intact.
 *
 * Deactivating flips `active=false`: the row stays listed here (greyed +
 * tagged INACTIVE) so an admin can reactivate it, but it drops out of the
 * active-only Warwick Landman picker. That is the intended way to retire
 * someone who has left — their name stays readable on the deals they worked.
 * A Popconfirm guards deactivation; reactivation is a single click.
 *
 * NAMES ARE UNIQUE (unlike Operators)
 * `warwick_landman.full_name` is a UNIQUE column, so both POST and PUT 409 on
 * a name already on the roster. Two responses to that here:
 *   * an EXACT match (trimmed) is blocked before the round-trip, naming the
 *     row it clashes with — and pointing at reactivation when that row is
 *     deactivated, since a twin is never the fix for "they are already in
 *     here";
 *   * a NEAR match (same name modulo case/punctuation/spacing) only warns.
 *     Whether the server treats "Bob Mcmahon" and "Bob McMahon" as the same
 *     name is the database column collation talking, not us, so we flag the
 *     look-alike and let the save go — a 409 comes back inline on the field
 *     if it was in fact the same name.
 *
 * NO AUDIT COLUMNS
 * Unlike operators and entities, a landman row carries no created_by /
 * created_at / updated_by / updated_at. The grid is therefore just Full Name,
 * Email and Active — do not add provenance columns expecting data behind them.
 *
 * Gating is UI/UX only — the de-hub `/landmen` endpoints are the real boundary
 * (POST and PUT are admin-only). We redirect non-admins to `/` as
 * defense-in-depth for the route, mirroring the Operators and AOIs pages.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { findNameMatches } from "../../../utils/normalizeLookupName";

const { Title } = Typography;

// Empty form template. `active` is rendered on the Edit form only (a new
// landman is always created active) but lives here so the form has one shape.
const EMPTY_LANDMAN = { full_name: "", email: "", active: true };

/** Trimmed, case-sensitive compare — the UNIQUE column's own rule. */
const isExactName = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

/**
 * LandmanFormModal — single Add/Edit modal. The parent owns visibility and the
 * row under edit; this component is layout + validation + the duplicate-name
 * notice only.
 *
 * `existingRows` is the full landman list from the parent, used to block an
 * exact duplicate name (which the server would 409 anyway) and to warn about
 * a look-alike.
 */
const LandmanFormModal = ({
  open,
  mode, // 'add' | 'edit'
  initialValues,
  saving,
  existingRows,
  editingId,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  // Mirror the name so the duplicate notice re-renders as the admin types.
  const typedName = Form.useWatch("full_name", form);

  // Re-seed the form whenever the modal opens or the row under edit changes.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_LANDMAN);
    }
  }, [open, initialValues, form]);

  // Landmen whose name looks like the one typed, excluding the row being
  // renamed. `full_name` is the compared column — the shared helper defaults
  // to `name`, which this table does not have.
  const looseMatches = useMemo(
    () => findNameMatches(typedName, existingRows, editingId, "full_name"),
    [typedName, existingRows, editingId]
  );

  // The subset the server is CERTAIN to reject: the same string after
  // trimming. The rest are look-alikes we only warn about (header comment).
  const exactMatches = useMemo(
    () => looseMatches.filter((row) => isExactName(row.full_name, typedName)),
    [looseMatches, typedName]
  );

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation / onSubmit errors surfaced inline by AntD or by the parent.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add landman" : "Edit landman"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_LANDMAN}>
        <Form.Item
          label="Full name"
          name="full_name"
          rules={[
            { required: true, message: "Full name is required" },
            // Blocking, like the AOIs screen: `full_name` is UNIQUE, so an
            // exact match is a guaranteed 409. Checking here saves the
            // round-trip and gives a message that says what to do instead.
            {
              validator: () =>
                exactMatches.length === 0
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        `"${exactMatches[0].full_name}" is already on the roster`
                      )
                    ),
            },
          ]}
          tooltip="The landman's name as it should appear in the deal form's Warwick Landman picker. Must be unique."
        >
          <Input autoFocus placeholder="e.g. Jane Whitfield" />
        </Form.Item>

        {/* Explains the block above, or flags a look-alike we let through. */}
        {looseMatches.length > 0 && (
          <Alert
            type={exactMatches.length > 0 ? "warning" : "info"}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              exactMatches.length > 0
                ? "This landman is already on the roster"
                : "A landman with a very similar name already exists"
            }
            description={
              <React.Fragment>
                <div style={{ marginBottom: 4 }}>
                  {exactMatches.length > 0
                    ? exactMatches.some((row) => !row.active)
                      ? "Landman names must be unique. The match below is deactivated — reactivate it from the grid instead of adding a second row, so the deals that person already worked stay attached to them."
                      : "Landman names must be unique. Edit the existing landman rather than adding a second row."
                    : "Not blocked — the names differ, so this may well be a different person. But the picker shows the name alone, so check this is not a duplicate of someone already listed."}
                </div>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                  {looseMatches.map((row) => (
                    <li key={row.id}>
                      {row.full_name}
                      {row.email ? (
                        <Typography.Text type="secondary">
                          {" "}
                          — {row.email}
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="secondary">
                          {" "}
                          — no email
                        </Typography.Text>
                      )}
                      {!row.active && (
                        <Tag color="default" style={{ marginLeft: 6 }}>
                          INACTIVE
                        </Tag>
                      )}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            }
          />
        )}

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: "email", message: "Enter a valid email address" }]}
          tooltip="Work email. Optional, and reference information only — access is granted by Entra app role, never by this row."
        >
          <Input placeholder="e.g. jane.whitfield@example.com (optional)" />
        </Form.Item>

        {/* Active is editable on the Edit form so an admin already in here to
            fix a name can retire the person in the same save. The grid row
            actions stay the quick path. */}
        {mode === "edit" && (
          <Form.Item
            label="Active"
            name="active"
            valuePropName="checked"
            tooltip="Inactive landmen stay on file and on their existing deals, but drop out of the Warwick Landman picker. This is the only kind of delete there is."
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const Landmen = () => {
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("admin.landmen");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listAllLandmen({
        token: tk,
        userOid,
        includeInactive,
      });
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading landmen:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  const handleCreate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.createLandman({
        token: tk,
        userOid,
        fullName: values.full_name?.trim(),
        // Blank is dropped from the body by the service — email is optional.
        email: values.email,
      });
      message.success("Landman created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      // `email` is always sent so the field can CLEAR an address: an explicit
      // null is distinguishable from an omitted key, which a partial PUT reads
      // as "leave this one alone".
      const addr = values.email?.trim();
      await dealsService.updateLandman({
        token: tk,
        userOid,
        id: editingRow.id,
        body: {
          full_name: values.full_name?.trim(),
          email: addr || null,
          active: Boolean(values.active),
        },
      });
      message.success("Landman saved");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error inline on the name field, or as a top-level
  // notification.
  //
  // A 409 from either endpoint means one thing — the `full_name` is already on
  // the roster — because it is the only UNIQUE column on the table. The modal
  // stays open with the server's own sentence under the name input; without
  // this the admin would get "Request failed with status code 409" and no clue
  // which field to change.
  const handleSaveError = (err, helpers) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409) {
      helpers?.setFieldError?.([
        { name: "full_name", errors: [String(detail)] },
      ]);
      return;
    }
    notification.error({
      message: "Landman save failed",
      description: String(detail),
    });
  };

  // Activate / deactivate — a soft toggle on `active`. No hard delete.
  const handleToggleActive = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateLandman({
        token: tk,
        userOid,
        id: row.id,
        body: { active: !row.active },
      });
      message.success(row.active ? "Landman deactivated" : "Landman activated");
      await refresh();
    } catch (err) {
      const detail = apiErrorText(err);
      notification.error({
        message: row.active
          ? "Failed to deactivate landman"
          : "Failed to activate landman",
        description: detail,
      });
    }
  };

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "full_name",
        title: "Full Name",
        key: "full_name",
        sorter: (a, b) =>
          String(a?.full_name ?? "").localeCompare(String(b?.full_name ?? "")),
        render: (value, row) =>
          row?.active ? value : <span style={{ opacity: 0.55 }}>{value}</span>,
      },
      {
        // Reference information only — a blank cell is normal, not missing
        // data. Access is granted by Entra app role, never by this column.
        dataIndex: "email",
        title: "Email",
        key: "email",
        sorter: (a, b) =>
          String(a?.email ?? "").localeCompare(String(b?.email ?? "")),
        render: (value, row) => {
          if (!value) {
            return (
              <Typography.Text type="secondary" italic>
                —
              </Typography.Text>
            );
          }
          return row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          );
        },
      },
      {
        dataIndex: "active",
        title: "Active",
        key: "active",
        width: 110,
        sorter: (a, b) => Number(a?.active) - Number(b?.active),
        render: (value) => (
          <Tag color={value ? "green" : "default"}>
            {value ? "ACTIVE" : "INACTIVE"}
          </Tag>
        ),
      },
      // No Created / Updated columns: `warwick_landman` has no audit columns.
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.full_name}`}
              />
              {row.active ? (
                <Popconfirm
                  title="Deactivate landman?"
                  description={`Deactivate "${row.full_name}"? They stay on the deals they already work, but will not appear in the Warwick Landman picker.`}
                  okText="Deactivate"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleToggleActive(row)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    aria-label={`Deactivate ${row.full_name}`}
                  />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(row)}
                  aria-label={`Activate ${row.full_name}`}
                />
              )}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initial values for the modal — for Edit we hydrate from the row. A null
  // email becomes "" so the Input stays controlled and clearing it round-trips
  // as an explicit null on save.
  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return {
        full_name: editingRow.full_name ?? "",
        email: editingRow.email ?? "",
        active: editingRow.active !== false,
      };
    }
    return EMPTY_LANDMAN;
  }, [modalMode, editingRow]);

  const gridHeight = Math.max(bounds.height - 220, 400);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Landmen
                  </Title>
                  <Space>
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    >
                      Show inactive
                    </Checkbox>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openAdd}
                    >
                      Add Landman
                    </Button>
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load landmen"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No landmen yet"
                    description="There are no landman records. Click Add Landman to create the first one."
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    showSearch
                    searchWidth={300}
                    showActionButton={false}
                    showExport
                    exportOptions={{ filename: "landmen" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                  />
                )}

                <LandmanFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  // The loaded list doubles as the duplicate-name lookup. It
                  // reflects the `includeInactive` toggle, so with inactive
                  // rows hidden the notice can only cite active landmen — the
                  // server still 409s either way, and the default is on.
                  existingRows={rows}
                  editingId={modalMode === "edit" ? editingRow?.id : undefined}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleEdit}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Landmen;
